import {
    IActiveTranslation,
    IAgent,
    IInteraction,
    IInteractionDetailsResponse,
    IInteractionMessage,
    IPhraseTriggerResult,
    IScorecardEvaluationSummary,
    ITag,
    ITagGroup,
    IUser,
    IWritingIssue,
    MessageDirection,
    Sentiment,
} from "@edgetier/watchtower-types";
import { PropsWithChildren } from "react";
import { AxiosError } from "axios";
import { IAutoMenuElement } from "../../../auto-menu/auto-menu.types";

/** Union type for API error responses */
type DataError = AxiosError<any> | string | null;

/** Generic interface for data fetching state with loading and error tracking */
interface IDataFetch<T> {
    data: T | null;
    isLoading: boolean;
    error?: DataError;
}
/** Extended data fetch interface for paginated results with next/previous page support */
export interface IPaginatedDataFetch<T> {
    data: T[] | null;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
    isLoadingNextPage: boolean;
    isLoadingPreviousPage: boolean;
    isLoading: boolean;
    error?: DataError;
    fetchNextPage?: () => void;
    fetchPreviousPage?: () => void;
}

/** Subset of interaction fields used in the sidebar contact history list */
export type IInteractionFromSameContactItem = Pick<IInteraction, "interactionId" | "startDateTime" | "interactionType">;

/** Configuration for a collapsible section in the details sidebar */
interface ISidebarSection {
    title: string;
    key: string;
    /**
     * Function to render the content of the sidebar section.
     * To avoid unnecessary re-computations, memoize the result using React.useMemo
     * in the consuming component, passing relevant dependencies.
     */
    content: (context: IInteractionModalContext) => React.ReactNode;
    isOpenByDefault?: boolean;
    isLoading?: boolean;
    header?: (context: IInteractionModalContext) => React.ReactNode;
    onToggleAccordion?: () => void;
}

export enum CurrentAudioMessagePosition {
    ABOVE = "above",
    BELOW = "below",
}

/** Complete context shape for the interaction modal, including data, actions, elements, and navigation */
export interface IInteractionModalContext {
    user: IDataFetch<IUser>;
    interaction: IDataFetch<IInteraction>; // TBI
    interactionWritingIssues: IDataFetch<{ [x: string]: IWritingIssue[] }>; // TBI
    interactionMessages: IDataFetch<IInteractionMessage[]> & {
        highlightedMessageId: string | null;
        setHighlightedMessageId: (id: string | null) => void;
        currentAudioMessageId: string | null;
        setCurrentAudioMessageId: (id: string | null) => void;
        isUserControllingScroll: boolean;
        setIsUserControllingScroll: (isUserInitiated: boolean) => void;
        currentAudioMessagePosition: CurrentAudioMessagePosition | null;
        setCurrentAudioMessagePosition: (value: CurrentAudioMessagePosition | null) => void;
        interactionActiveSentiment: Record<string, string[]>;
        setInteractionActiveSentiment: (sentiment: Record<string, string[]>) => void;
    }; // TBI
    interactionMessagesAgents: IDataFetch<IAgent[]>; // TBI
    interactionMessagesTagGroups: IDataFetch<ITagGroup[]>; // TBI
    interactionDetails: IDataFetch<IInteractionDetailsResponse>; // TBI
    interactionsFromSameContact?: IPaginatedDataFetch<IInteractionFromSameContactItem>;
    interactionsFromSameContactNavigation?: {
        onClick: (interactionId: IInteractionFromSameContactItem["interactionId"]) => void;
    };
    elements?: {
        audio?: {
            audioPlayer?: () => React.ReactElement;
            messageAudioSeek?: (props: {
                sendDateTime: Date;
                messageDirection: MessageDirection;
                isVisible: boolean;
            }) => React.ReactNode;
            audioProvider?: (props: PropsWithChildren) => React.ReactNode;
            highlightAudioMessage?: () => React.ReactNode;
            followConversationButton?: () => React.ReactNode;
        };
        sentiments?: {
            tooltipContent?: (props: {
                sentiments: Partial<Record<Sentiment, boolean>>;
                messageId: string;
            }) => React.ReactNode;
            navigationToolbar?: () => React.ReactNode;
        };
        tags?: {
            tooltipContent?: (props: {
                tag: string;
                tagId: string;
                tagGroup: string;
                messageId: string;
                onMouseEnter?: (phraseTriggerDetails?: IPhraseTriggerResult) => void;
                onMouseLeave?: (phraseTriggerDetails?: IPhraseTriggerResult) => void;
            }) => React.ReactNode;
            editTagButton?: (props: { tagId: string; messageId: string }) => React.ReactNode;
            removeTagButton?: (props: { tag: ITag; messageId: string }) => React.ReactNode;
            addTagButton?: (props: { messageId: string }) => React.ReactNode;
        };
    };
    actions?: {
        moreActions?:
            | {
                  items: IAutoMenuElement[];
              }
            | { menu: React.ReactNode };
        customerOverview?: {
            onClick: () => Promise<void>;
            isDisabled: boolean;
        };
        writingIssues?: {
            onToggle: () => Promise<void>;
            isEnabled: boolean;
        };
        translation?: {
            onToggle: () => Promise<void>;
            toggleMessageTranslation: (
                interactionMessageId: string,
                text: string,
                languageIsoCode: string
            ) => Promise<void>;
            activeTranslations: Record<string, IActiveTranslation>;
            isEnabled: boolean;
            isDisabled: boolean;
        };
        sentiments?: {
            onToggle: (messageId: string, sentiment: Sentiment) => Promise<void>;
            isLoading: boolean;
        };
        tracking?: {
            trackUser: (interactionId: string) => void;
        };
    };
    getContactUrl?: (contactId: string) => Promise<string>; // TBI
    getViewReviewUrl?: (reviewSummary: IScorecardEvaluationSummary) => Promise<string>; // TBI
    onStartReview?: () => Promise<void>; // TBI
    onSentimentToggle?: (interactionMessage: IInteractionMessage, sentiment: Sentiment) => Promise<void>; // TBI
    interactionNavigation?: {
        currentIndex: number;
        total: number;
        showInteractionCount: boolean;
        isLoading: boolean;
        hasNext: boolean;
        onPrevious: () => void;
        onNext: () => void;
    };
    permissions: {
        interactionSummary: boolean;
        viewReviews: boolean;
        startReviews: boolean;
    };
    sidebar: {
        sections?: ISidebarSection[];
    };
}
